import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import ScrollToTop from './ScrollToTop';
import { useRedditPixel } from '../hooks/useRedditPixel';
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { initPublicPixels } from '../lib/publicPixels';

// LandingPage is critical for LCP, load immediately
import LandingPage from './LandingPage';

// Lazy load all other public routes to reduce initial bundle size
const PublicEstimateView = lazy(() => import('../features/public/PublicEstimateView').then(m => ({ default: m.PublicEstimateView })));
const PublicInvoiceView = lazy(() => import('../features/public/PublicInvoiceView').then(m => ({ default: m.PublicInvoiceView })));
const PaymentSuccess = lazy(() => import('../features/public/PaymentSuccess'));
const StripeReturn = lazy(() => import('../features/public/StripeReturn'));
const EmailVerificationSuccess = lazy(() => import('./EmailVerificationSuccess'));
const PricingPage = lazy(() => import('./PricingPage'));
const AboutPage = lazy(() => import('./AboutPage'));
const BlogPage = lazy(() => import('./BlogPage'));
const FeaturesPage = lazy(() => import('./FeaturesPage'));
const FAQsPage = lazy(() => import('./FAQsPage'));
const SupportPage = lazy(() => import('./SupportPage'));
const AIAssistantPage = lazy(() => import('./AIAssistantPage'));
const ResetPassword = lazy(() => import('./ResetPassword'));
const PWAWelcome = lazy(() => import('./PWAWelcome'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const EULA = lazy(() => import('./EULA'));
const CookiePolicy = lazy(() => import('./CookiePolicy'));
const TermsOfService = lazy(() => import('./TermsOfService'));
const QuickBooksCallback = lazy(() => import('../pages/integrations/quickbooks/callback'));
const OptinWorkflowTwilio = lazy(() => import('../pages/OptinWorkflowTwilio'));
const PrivacyRightsPage = lazy(() => import('./PrivacyRightsPage'));
const DepositPaymentReturn = lazy(() => import('../features/public/DepositPaymentReturn'));
const QuickQuotePage = lazy(() => import('../features/quickquote/QuickQuotePage'));

// Feature Pages - Lazy loaded
const EstimatesInvoicesPage = lazy(() => import('./features/EstimatesInvoicesPage'));
const ClientManagementPage = lazy(() => import('./features/ClientManagementPage'));
const JobTrackingPage = lazy(() => import('./features/JobTrackingPage'));
const EmployeeManagementPage = lazy(() => import('./features/EmployeeManagementPage'));
const MobileAppPage = lazy(() => import('./features/MobileAppPage'));
const PaymentProcessingPage = lazy(() => import('./features/PaymentProcessingPage'));
const EmbeddableWidgetsPage = lazy(() => import('./features/EmbeddableWidgetsPage'));
const SchedulingCalendarPage = lazy(() => import('./features/SchedulingCalendarPage'));
const AutomationsPage = lazy(() => import('./features/AutomationsPage'));
const FileManagementPage = lazy(() => import('./features/FileManagementPage'));
const AIPoweredToolsPage = lazy(() => import('./features/AIPoweredToolsPage'));
const SiteAnalysisToolsPage = lazy(() => import('./features/SiteAnalysisToolsPage'));
const ServicesPackagesPage = lazy(() => import('./features/ServicesPackagesPage'));
const ExpenseManagementPage = lazy(() => import('./features/ExpenseManagementPage'));
const ReportsAnalyticsPage = lazy(() => import('./features/ReportsAnalyticsPage'));
const ToolBeltPage = lazy(() => import('./features/ToolBeltPage'));

// Loading fallback component
const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <div className="text-center">
      <div className="w-8 h-8 rounded-full border-4 border-teal-200 border-t-teal-500 animate-spin mb-4 mx-auto"></div>
      <p className="text-secondary">Loading...</p>
    </div>
  </div>
);

export const PublicRoutes = () => {
  // Load marketing pixels (GA4, Meta, Reddit) only on public routes.
  // Why: their auto-fired beacons and Facebook iframe/form-action violate the
  // strict CSP we apply to authenticated routes.
  useEffect(() => {
    initPublicPixels();
  }, []);

  // Track Reddit Pixel page visits on public pages
  useRedditPixel();

  // Track Google Analytics page views on public pages
  useGoogleAnalytics();

  // Track Meta Pixel page views on public pages
  useMetaPixel();

  // Ensure theme variables don't affect public routes
  useEffect(() => {
    const html = document.documentElement;
    // Remove data-theme attribute to prevent theme CSS from affecting public routes
    html.removeAttribute('data-theme');
    // Remove dark class if present
    html.classList.remove('dark');
    
    // Cleanup: restore theme when component unmounts (user navigates to protected routes)
    return () => {
      // Don't restore here - let ProtectedRoutes handle it
    };
  }, []);

  return (
    <>
      <ScrollToTop />
      {/* Vercel Analytics - only on public pages, not private/protected routes */}
      <Analytics />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/pwa-welcome" element={<PWAWelcome />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/faqs" element={<FAQsPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/prosperia-ai" element={<AIAssistantPage />} />
          <Route path="/public/estimate/:token" element={<PublicEstimateView />} />
          <Route path="/public/invoice/:token" element={<PublicInvoiceView />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/deposit-success" element={<DepositPaymentReturn />} />
          <Route path="/stripe-return" element={<StripeReturn />} />
          <Route path="/email-verification" element={<EmailVerificationSuccess />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/integrations/quickbooks/callback" element={<QuickBooksCallback />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/eula" element={<EULA />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/optin-workflow-twilio" element={<OptinWorkflowTwilio />} />
          <Route path="/privacy-rights" element={<PrivacyRightsPage />} />
          <Route path="/quickquote" element={<QuickQuotePage />} />

          {/* Feature Detail Pages */}
          <Route path="/features/estimates-invoices" element={<EstimatesInvoicesPage />} />
          <Route path="/features/client-management" element={<ClientManagementPage />} />
          <Route path="/features/job-tracking" element={<JobTrackingPage />} />
          <Route path="/features/employee-management" element={<EmployeeManagementPage />} />
          <Route path="/features/mobile-app" element={<MobileAppPage />} />
          <Route path="/features/payment-processing" element={<PaymentProcessingPage />} />
          <Route path="/features/embeddable-widgets" element={<EmbeddableWidgetsPage />} />
          <Route path="/features/scheduling-calendar" element={<SchedulingCalendarPage />} />
          <Route path="/features/automations" element={<AutomationsPage />} />
          <Route path="/features/file-management" element={<FileManagementPage />} />
          <Route path="/features/ai-powered-tools" element={<AIPoweredToolsPage />} />
          <Route path="/features/site-analysis-tools" element={<SiteAnalysisToolsPage />} />
          <Route path="/features/services-packages" element={<ServicesPackagesPage />} />
          <Route path="/features/expense-management" element={<ExpenseManagementPage />} />
          <Route path="/features/reports-analytics" element={<ReportsAnalyticsPage />} />
          <Route path="/features/tool-belt" element={<ToolBeltPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
};